import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { Customer, SalesLine, SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useEffect, useRef } from 'react';
import { useDatalayerService } from './useDatalayerService';

interface UseOrderConfirmationDatalayerParams {
    request: IRequestContext;
    telemetry: ITelemetry;
    salesOrder: SalesOrder | undefined;
    products: SimpleProduct[];
    customer: Customer | undefined;
}

export const useOrderConfirmationDatalayer = ({
    request,
    telemetry,
    salesOrder,
    products,
    customer
}: UseOrderConfirmationDatalayerParams): void => {
    const datalayerService = useDatalayerService({ request, telemetry });
    const hasFiredIdentityRef = useRef(false);
    const hasFiredPurchaseRef = useRef(false);

    useEffect(() => {
        if (hasFiredIdentityRef.current || !salesOrder) {
            return;
        }
        hasFiredIdentityRef.current = true;

        // Order is confirmed — clear stored guest checkout identity
        datalayerService.clearGuestCheckoutIdentity();

        datalayerService.pushOrderConfirmationIdentity(
            { ReceiptEmail: salesOrder.ReceiptEmail, ShippingAddress: salesOrder.ShippingAddress ?? undefined },
            customer
        );
    }, [salesOrder, customer, datalayerService]);

    useEffect(() => {
        if (hasFiredPurchaseRef.current || !salesOrder || !products.length) {
            return;
        }
        hasFiredPurchaseRef.current = true;

        const salesLines: SalesLine[] = salesOrder.SalesLines ?? [];
        datalayerService.pushPurchase(salesLines, products, salesOrder.Id, salesOrder.TotalAmount);
    }, [salesOrder, products, datalayerService]);
};
